import React, { FC, useEffect, useMemo, useRef } from 'react';
import { AutoComplete } from 'antd';
import debounce from 'lodash.debounce';
import { DEFAULT_DEBOUNCE_DELAY, SELECT_LIST_HEIGHT_320 } from 'common/config';
import { mapToMenuWithDivider } from 'common/helpers';
import { Close } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getAddressSuggestions, selectAddressSuggestions } from 'entities/Address/Address.slice';
import { IAddressSuggestion } from 'entities/Address/Address.models';

interface IAntdFormControl {
  onChange?: (value?: any) => void;
  value?: any;
}

interface IComponentProps {
  placeholder?: string;
  onSelect?: (suggestion: IAddressSuggestion) => void;
}

export const AddressSuggestionSelector: FC<IComponentProps & IAntdFormControl> = ({ placeholder, onSelect, value, onChange }) => {
  const dispatch = useAppDispatch();
  // address
  const addressSuggestions = useAppSelector(selectAddressSuggestions);

  const skipNextChangeRef = useRef(false);
  const debouncedSearch = useRef(
    debounce((val?: string) => dispatch(getAddressSuggestions({ name: val ?? '' })), DEFAULT_DEBOUNCE_DELAY),
  ).current;
  const addressSuggestionOptions = useMemo(() => {
    return mapToMenuWithDivider(addressSuggestions.map((item) => ({ value: item.name })));
  }, [addressSuggestions]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const handleSelect = (val?: string): void => {
    const suggestion = addressSuggestions.find((i) => i.name === val);

    if (!suggestion) {
      return;
    }

    skipNextChangeRef.current = true;

    onChange?.(suggestion);
    onSelect?.(suggestion);
  };

  const handleChange = (inputValue?: string): void => {
    if (skipNextChangeRef.current) {
      skipNextChangeRef.current = false;
      return;
    }

    onChange?.(!inputValue || !inputValue.length ? undefined : inputValue);
    debouncedSearch(inputValue);
  };

  return (
    <AutoComplete
      popupClassName="redesign"
      placeholder={placeholder}
      virtual={false}
      listHeight={SELECT_LIST_HEIGHT_320}
      showSearch
      allowClear={{ clearIcon: <Close className="icon-close-input" /> }}
      options={addressSuggestionOptions}
      value={value}
      onChange={handleChange}
      onSelect={handleSelect}
    />
  );
};
